
import { Companies, Invoices } from "@/api";
import PageTitle from "@/components/common/PageTitle.vue";
import { downloadFile } from "@/utils/helpers";
import moment from "moment";
import { Component, Vue } from "vue-property-decorator";
import { namespace } from "vuex-class";

const Auth = namespace("auth");

@Component({ components: { PageTitle } })
export default class InvoicesPage extends Vue {
  @Auth.Getter("getUser") getUser!: any;
  pageHeaders = [{ icon: "", header: "Event Invoices" }];
  filterButtons = [
    {
      text: "Reset filters",
      icon: "",
      color: "gray",
      action: "reset"
    },
    {
      text: "Apply filters",
      icon: "",
      color: "dark",
      action: "filter"
    }
  ];
  currentDate = moment().format("YYYY-MM-DD");
  loading = false;
  downloading = false;
  approving = false;
  tab = 0;
  fromMenu = false;
  toMenu = false;
  dateFrom: string | null = moment()
    .startOf("month")
    .format("YYYY-MM-DD");
  dateTo: string | null = moment().format("YYYY-MM-DD");
  selectedInstructor: number | null = null;
  selectedCustomer: number | null = null;
  invoiceDialog = false;
  invoices: any[] = [];
  knownInstructors: any[] = [];
  knownCustomers: any[] = [];
  activeInvoice: any = null;

  get dateFromText(): string {
    return this.dateFrom ? moment(this.dateFrom).format("DD/MM/YYYY") : "";
  }

  get dateToText(): string {
    return this.dateTo ? moment(this.dateTo).format("DD/MM/YYYY") : "";
  }

  headers = [
    { text: "Event", value: "eventId", align: "start", width: "85px" },
    {
      text: "Invoice no.",
      value: "invoiceNumber",
      align: "start",
      width: "110px"
    },
    { text: "Invoice", value: "type", align: "start", width: "120px" },
    { text: "Quote ID", value: "quoteId", align: "start", width: "100px" },
    {
      text: "Booking start",
      value: "trainingDate",
      align: "start",
      width: "135px"
    },
    {
      text: "Booking end",
      value: "finishDate",
      align: "start",
      width: "135px"
    },
    { text: "Course", value: "courseName", align: "start" },
    { text: "Company", value: "customerName", align: "start" },
    { text: "Instructor", value: "instructorName", align: "start" },
    { text: "Total payable", value: "total", align: "start" },
    { text: "Status", value: "status", align: "start" },
    {
      text: "Actions",
      value: "actions",
      align: "start",
      sortable: false,
      width: "145px"
    }
  ];

  get instructorOptions(): any[] {
    return this.knownInstructors;
  }

  get customerOptions(): any[] {
    return this.knownCustomers;
  }

  get invoiceTotal(): number {
    if (!this.activeInvoice) return 0;
    return [
      this.activeInvoice.courseAmount,
      this.activeInvoice.fuelAmount,
      this.activeInvoice.sntAmount,
      this.activeInvoice.breakfastAmount,
      this.activeInvoice.dinnerAmount,
      this.activeInvoice.tollsAmount,
      this.activeInvoice.otherAmount
    ].reduce((sum, value) => sum + Number(value || 0), 0);
  }

  async created(): Promise<void> {
    try {
      const companies = await Companies.listWithoutPagination();
      this.knownCustomers = companies.map((company: any) => ({
        id: company.id,
        name: company.name
      }));
    } catch (e) {
      await this.$error(e);
    }
    await this.loadInvoices();
  }

  async loadInvoices(): Promise<void> {
    this.loading = true;
    try {
      const invoices = await Invoices.list({
        instructorId: this.selectedInstructor || undefined,
        customerId: this.selectedCustomer || undefined,
        dateFrom: this.dateFrom || undefined,
        dateTo: this.dateTo || undefined,
        status: this.tab === 1 ? "approved" : "pending"
      });
      this.invoices = invoices;
      this.rememberFilterOptions(invoices);
    } catch (e) {
      await this.$error(e);
    } finally {
      this.loading = false;
    }
  }

  async resetFilters(): Promise<void> {
    this.dateFrom = moment()
      .startOf("month")
      .format("YYYY-MM-DD");
    this.dateTo = this.currentDate;
    this.selectedInstructor = null;
    this.selectedCustomer = null;
    await this.loadInvoices();
  }

  rememberFilterOptions(invoices: any[]): void {
    invoices.forEach(invoice => {
      if (
        invoice.instructorId &&
        !this.knownInstructors.some(item => item.id === invoice.instructorId)
      ) {
        this.knownInstructors.push({
          id: invoice.instructorId,
          name: invoice.instructorName
        });
      }
      (invoice.customers || []).forEach((customer: any) => {
        if (!this.knownCustomers.some(item => item.id === customer.id)) {
          this.knownCustomers.push(customer);
        }
      });
    });
  }

  openInvoice(invoice: any): void {
    this.activeInvoice = JSON.parse(JSON.stringify(invoice));
    this.invoiceDialog = true;
  }

  closeInvoice(): void {
    this.invoiceDialog = false;
    this.activeInvoice = null;
  }

  get invoiceLines(): any[] {
    const invoice = this.activeInvoice;
    if (!invoice) return [];

    const days = (value: number) => {
      const qty = Number(value || 0);
      return `${qty} ${qty === 1 ? "day" : "days"}`;
    };
    const lines = [];

    if (invoice.isOutsource) {
      lines.push({
        label: "Facilitation fee",
        qty: days(invoice.courseQty),
        rate: invoice.courseRate,
        amount: invoice.courseAmount
      });
    }

    lines.push(
      {
        label: "Fuel Claimed",
        qty: `${Number(invoice.kilometersClaimed || 0).toFixed(2)} km`,
        rate: invoice.fuelRate,
        amount: invoice.fuelAmount
      },
      {
        label: "SNT",
        qty: days(invoice.sntNights),
        rate: invoice.sntRate,
        amount: invoice.sntAmount
      },
      {
        label: "Breakfast Allowance",
        qty: days(invoice.breakfastQty),
        rate: invoice.breakfastRate,
        amount: invoice.breakfastAmount
      },
      {
        label: "Dinner Allowance",
        qty: days(invoice.dinnerQty),
        rate: invoice.dinnerRate,
        amount: invoice.dinnerAmount
      },
      {
        label: "Toll Gates",
        qty: "-",
        rate: null,
        amount: invoice.tollsAmount
      },
      {
        label: "Other",
        qty: "-",
        rate: null,
        amount: invoice.otherAmount
      }
    );

    return lines;
  }

  get canApprove(): boolean {
    return Boolean(
      this.getUser &&
        this.getUser.permissions &&
        this.getUser.permissions.includes("approve_invoices")
    );
  }

  async approveInvoice(): Promise<void> {
    this.approving = true;
    try {
      const data = await Invoices.approve(
        this.activeInvoice.eventId,
        this.activeInvoice.type
      );
      // A booking can carry both a prepayment and a final invoice, so only the
      // row that was just approved leaves the pending list.
      this.invoices = this.invoices.filter(
        item => !(item.eventId === data.eventId && item.type === data.type)
      );
      this.activeInvoice = JSON.parse(JSON.stringify(data));
      await this.$success(
        "<strong>Success!</strong> Invoice has been approved."
      );
    } catch (e) {
      await this.$error(e);
    } finally {
      this.approving = false;
    }
  }

  async downloadInvoice(): Promise<void> {
    this.downloading = true;
    try {
      const { blob, fileName } = await Invoices.download(
        this.activeInvoice.eventId,
        this.activeInvoice.type
      );
      downloadFile(blob, fileName, false);
    } catch (e) {
      await this.$error(e);
    } finally {
      this.downloading = false;
    }
  }

  formatMoney(value: number): string {
    return `R ${new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Number(value || 0))}`;
  }
}
